import {Drawer} from '@mui/material';
import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
import {Button} from '@wandb/weave/components/Button';
import {Checkbox, type CheckboxState} from '@wandb/weave/components/Checkbox';
import {Icon} from '@wandb/weave/components/Icon';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {produce} from 'immer';
import React, {
  type FC,
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {useHistory} from 'react-router-dom';
import {type DropdownProps, Popup} from 'semantic-ui-react';

import introGraphicNewFindings from '../../assets/intro-report-graphic-new-findings.svg';
import introGraphicNoteToSelf from '../../assets/intro-report-graphic-note-to-self.svg';
import introGraphicWorkLog from '../../assets/intro-report-graphic-work-log.svg';
import {
  useEntityProjectsReportsQuery,
  useProjectReportsQuery,
} from '../../generated/graphql';
import {Analytics} from '../../services/analytics';
import {useApolloClient} from '../../state/hooks';
import {
  PanelBankContext,
  PanelBankUpdaterContext,
} from '../../state/panelbank/context';
import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import {pushReport} from '../../state/reports/hooks';
import {
  appendSectionsToReport,
  editReportPanelGroups,
} from '../../state/reports/reportSections';
import {RunQueryContext} from '../../state/runs/context';
import {useViewer} from '../../state/viewer/hooks';
import * as ViewHooks from '../../state/views/hooks';
import {useRunSets} from '../../state/views/runSet/hooks';
import * as Filters from '../../util/filters';
import type * as FilterTypes from '../../util/filterTypes';
import {
  hideSearchRegexMatchesPanel,
  searchRegexMatchesPanel,
} from '../../util/panelbank';
import {
  getFullWidthPanelLayout,
  getNewGridItemLayout,
} from '../../util/panelbankGrid';
import {type LayoutParameters, type Panel} from '../../util/panelTypes';
import * as Report from '../../util/report';
import {type CreateReportOpts} from '../../util/report';
import type * as RunTypes from '../../util/runTypes';
import {emptyReportRunSetSelectAll} from '../../util/section';
import {
  CREATED_FIRST_REPORT_FEATURE_KEY,
  useOnboardingContext,
} from '../../util/userContext';
import * as AddToReportModal from '../AddToReportModal';
import {useDefinedMetricContext} from '../PanelBank/contextProviders/DefinedMetricContext';
import DraftWarningModal, {
  type DraftWarningModalProps,
} from '../ReportModals/DraftWarningModal';
import {isPanelGrid} from '../Slate/plugins/panel-grid';
import {usePanelsSharedRefsContext} from '../Views/contexts/PanelsSharedRefsContext';
import * as S from '../WBModal.styles';
import {useSemanticLegendSettingsContext} from '../WorkspaceDrawer/Settings/semanticLegend/SemanticLegendSettingsWithCategoricalConfigContextProvider';
import {RunColorOptions} from '../WorkspaceDrawer/Settings/types';
import {ReportImportPanelLimitBanner} from './ReportImportPanelLimitBanner';
import ReportSectionPicker, {NEW_SECTION_IDX} from './ReportSectionPicker';
import {
  type CreateReportModalProps,
  type CreateReportParams,
  type SrcSectionOption,
} from './types';
import {
  getFirstPagePanels,
  getSelectedImportPanelCount,
  MAX_REPORT_IMPORT_PANELS,
  normalizePanelLayouts,
  takeFirstPanelsBySection,
} from './utils';

const NEW_REPORT_ID = 'new';

type ModalPage = 'intro' | 'import' | 'export';

const CreateReportModalContentComp: FC<CreateReportModalProps> = ({
  inheritedSettings,
  useExportableSections,
  panelIsActive,
}) => {
  const history = useHistory();
  const client = useApolloClient();
  const viewer = useViewer();
  const {
    entityName,
    projectName,
    mergeFilters,
    runSetName,
    report: currentReport,
  } = useContext(RunQueryContext);

  const {
    data: currentProjectReportsQuery,
    loading: isLoadingCurrentProjectReports,
    error: currentProjectReportsError,
  } = useProjectReportsQuery({variables: {entityName, projectName}});

  if (currentProjectReportsError) {
    console.error(currentProjectReportsError);
  }

  if (entityName == null || projectName == null) {
    throw new Error('missing RunQueryContext info');
  }
  const {definedGlobs} = useDefinedMetricContext();

  const {exportingPanelRefID, exportingSectionRefID} =
    useContext(PanelBankContext);
  const isSinglePanelExport = exportingPanelRefID != null;
  const exportingPanelOrSection =
    isSinglePanelExport || exportingSectionRefID != null;
  const {
    setExportingUnknownID,
    setExportingPanelRefID,
    setExportingSectionRefID,
  } = useContext(PanelBankUpdaterContext);
  const {onboardingState, setOnboardingState} = useOnboardingContext(
    CREATED_FIRST_REPORT_FEATURE_KEY
  );

  const {customRunColorsRef, customRunNamesRef, runSetRefs} =
    usePanelsSharedRefsContext();
  const {semanticLegendSettings, isMetricBasedColors} =
    useSemanticLegendSettingsContext();
  const sectionsWithVisiblePanels = useExportableSections();
  // Get the current search regex directly from context to ensure we have the latest value
  const {
    debouncedHideSearchRegex: currentHideSearchRegex,
    debouncedSearchRegex: currentSearchRegex,
  } = usePanelSearchDebouncedContext();
  const customRunColors = ViewHooks.usePart(customRunColorsRef);
  const customRunNames = ViewHooks.usePartMaybe(customRunNamesRef);
  const runSets = useRunSets(runSetRefs);

  // Helper to filter panels by the current search and hide regexes
  function filterBySearchRegex<T extends Panel>(panels: readonly T[]): T[] {
    return panels.filter(p => {
      const isSearchMatch =
        currentSearchRegex == null ||
        searchRegexMatchesPanel(currentSearchRegex, p);
      const isHideMatch =
        currentHideSearchRegex != null &&
        hideSearchRegexMatchesPanel(currentHideSearchRegex, p);
      return isSearchMatch && !isHideMatch;
    });
  }

  const [modalPage, setModalPage] = useState<ModalPage>('import');
  const [creatingReport, setCreatingReport] = useState(false);
  const [reportSearchQuery, setReportSearchQuery] = useState('');
  const [destinationProjectSearchStr, setDestinationProjectSearchStr] =
    useState('');
  const [draftWarningModalOpen, setDraftWarningModalOpen] = useState(false);
  const [srcSectionOptions, setSrcSectionOptions] =
    useState<SrcSectionOption[]>(getSrcSectionOptions);
  const [freezeRunSet, setFreezeRunSet] = useState(false);

  const entityOptions = useMemo(() => {
    return (
      viewer?.teams?.edges.map(e => ({
        text: e.node?.name ?? '',
        value: e.node?.name ?? '',
      })) ?? []
    );
  }, [viewer]);

  const defaultEntity =
    entityOptions.find(e => e.value === entityName)?.value ??
    entityOptions[0].value;
  const [selectedEntity, setSelectedEntity] = useState(defaultEntity);
  const [selectedProjectID, setSelectedProjectID] = useState<string>('');
  const [selectedReportID, setSelectedReportID] = useState(NEW_REPORT_ID);
  const [selectedDstSectionIdx, setSelectedDstSectionIdx] =
    useState(NEW_SECTION_IDX);
  const [selectAllSectionOptions, setSelectAllSectionOptions] =
    useState<CheckboxState>('indeterminate');

  const addingToReportSection =
    isSinglePanelExport && selectedDstSectionIdx !== NEW_SECTION_IDX;

  const currentDate = useMemo(() => new Date(), []);

  const onEntityChange = useCallback(
    (e: React.SyntheticEvent, data: DropdownProps) => {
      setSelectedEntity(data.value as string);
      setSelectedProjectID('');
      setSelectedReportID(NEW_REPORT_ID);
      setDestinationProjectSearchStr('');
    },
    []
  );

  const onProjectChange = useCallback(
    (e: React.SyntheticEvent, data: DropdownProps) => {
      setSelectedProjectID(data.value as string);
      setSelectedReportID(NEW_REPORT_ID);
    },
    []
  );
  const onReportChange = useCallback(
    (e: React.SyntheticEvent, data: DropdownProps) => {
      setSelectedReportID(data.value as string);
      setReportSearchQuery('');
      setSelectedDstSectionIdx(NEW_SECTION_IDX);
    },
    []
  );
  const onDstSectionChange = useCallback(
    (i: number) => setSelectedDstSectionIdx(i),
    []
  );

  const onCloseModal = useCallback(() => {
    setExportingUnknownID(false);
    setExportingPanelRefID(null);
    setExportingSectionRefID(null);
  }, [setExportingUnknownID, setExportingPanelRefID, setExportingSectionRefID]);

  const entityProjectsQuery = useEntityProjectsReportsQuery({
    variables: {
      entityName: selectedEntity,
      pattern: destinationProjectSearchStr,
      first: 100,
    },
    skip: selectedEntity === '',
  });

  const {projectOptions, reportOptionsByProjectID, projectInfoByID} =
    AddToReportModal.useAddToReportOptionsByEntity({
      entityProjectsReportsQuery: entityProjectsQuery,
      currentReport,
      currentProjectReportsQuery,
    });

  // set modal page based on whether user is adding to or creating their first report
  useLayoutEffect(() => {
    if (exportingPanelOrSection) {
      setModalPage('export');
    } else if (!onboardingState.createdFirstReport) {
      setModalPage('intro');
    }
  }, [exportingPanelOrSection, onboardingState.createdFirstReport]);

  // Select a source section by default
  // this appears to be broken?
  useEffect(() => {
    if (srcSectionOptions.length === 0) {
      return;
    }

    if (exportingSectionRefID == null) {
      selectSrcSectionOption(srcSectionOptions[0]);
      return;
    }

    const currentSectionOpt = srcSectionOptions.find(
      s => s.ref.id === exportingSectionRefID
    );
    if (currentSectionOpt != null) {
      selectSrcSectionOption(currentSectionOpt);
    }

    // eslint-disable-next-line
  }, []);
  const loading = entityProjectsQuery.loading || isLoadingCurrentProjectReports;
  // Select current project by default
  useEffect(() => {
    if (
      entityProjectsQuery.data == null ||
      entityProjectsQuery.loading ||
      isLoadingCurrentProjectReports
    ) {
      return;
    }
    if (selectedProjectID !== '') {
      return;
    }

    const projects =
      AddToReportModal.getProjectsFromEntityProjectsReportsQuery(
        entityProjectsQuery
      );
    if (projects.length === 0) {
      return;
    }

    // If a project on the list is already selected, don't change it against the user's will
    let projo = projects.find(p => p.id === selectedProjectID);
    // otherwise, select current project
    if (projo == null) {
      projo = currentProjectReportsQuery?.project ?? undefined;
    }
    // If current project is not on the list, default to first project
    if (projo == null) {
      projo = projects[0];
    }
    setSelectedProjectID(projo.id);
    // eslint-disable-next-line
  }, [
    entityProjectsQuery,
    entityName,
    projectName,
    currentProjectReportsQuery,
    isLoadingCurrentProjectReports,
  ]);

  const applyIntroModalWidth = modalPage === 'intro' ? '670px' : undefined;
  const applyImportModalWidth = modalPage === 'import' ? '65vw' : undefined;

  const selectedImportPanelCount =
    getSelectedImportPanelCount(srcSectionOptions);
  const showPanelLimitWarning =
    selectedImportPanelCount > MAX_REPORT_IMPORT_PANELS;

  return (
    <>
      <Drawer anchor="right" open={true} onClose={onCloseModal}>
        {modalPage === 'export' && renderExportModal()}
      </Drawer>
      {modalPage !== 'export' && (
        <S.WBModal
          className="save-snapshot-modal"
          maxWidth={applyImportModalWidth}
          width={applyIntroModalWidth}
          open={true}
          onClose={onCloseModal}>
          {modalPage === 'intro' && renderIntroModal()}
          {modalPage === 'import' && renderImportModal()}
        </S.WBModal>
      )}

      {draftWarningModalOpen && selectedReportID !== NEW_REPORT_ID && (
        <DraftWarningModal
          parentViewID={selectedReportID}
          flipButtonColors
          onNoDraftFound={() => createReportInSelectedProject(selectedReportID)}
          onDiscard={() => createReportInSelectedProject(selectedReportID)}
          onClose={() => setDraftWarningModalOpen(false)}
          {...getDraftWarningModalProps()}
        />
      )}
    </>
  );

  function getSrcSectionOptions(): SrcSectionOption[] {
    return sectionsWithVisiblePanels.map(s => {
      let totalPanels = s.panels;
      if (panelIsActive != null) {
        totalPanels = totalPanels.filter(p => panelIsActive(s, p));
      }
      totalPanels = filterBySearchRegex(totalPanels);
      const firstPagePanels = getFirstPagePanels(
        s.name,
        s.flowConfig,
        totalPanels
      );
      return {
        ...s,
        visiblePanelCount: firstPagePanels.length,
        totalPanelCount: totalPanels.length,
        selected: false,
        visibleOnly: true,
      };
    });
  }

  function toggleSrcSectionOption(
    o: SrcSectionOption,
    toggleVisibleOnly?: true
  ) {
    setSrcSectionOptions(prev =>
      produce(prev, draft => {
        for (const s of draft) {
          if (s.ref.id !== o.ref.id) {
            continue;
          }
          if (toggleVisibleOnly) {
            s.visibleOnly = !s.visibleOnly;
          } else {
            s.selected = !s.selected;
          }
          checkIfSelectAllShouldBeIndeterminate(draft);
          return;
        }
      })
    );
  }

  function selectSrcSectionOption(o: SrcSectionOption) {
    if (!o.selected) {
      toggleSrcSectionOption(o);
    }
  }

  function setAllSrcSectionOptions(selected: boolean) {
    setSrcSectionOptions(srcSectionOptions.map(opt => ({...opt, selected})));
  }

  function toggleSelectAllSectionOptions() {
    if (selectAllSectionOptions === false) {
      setSelectAllSectionOptions(true);
      setAllSrcSectionOptions(true);
    } else {
      setSelectAllSectionOptions(false);
      setAllSrcSectionOptions(false);
    }
  }

  function checkIfSelectAllShouldBeIndeterminate(
    srcSectionOptionsDraft: SrcSectionOption[]
  ) {
    const allSelected = srcSectionOptionsDraft.every(o => o.selected);
    const noneSelected = srcSectionOptionsDraft.every(o => !o.selected);
    if (allSelected) {
      setSelectAllSectionOptions(true);
    } else if (noneSelected) {
      setSelectAllSectionOptions(false);
    } else {
      setSelectAllSectionOptions('indeterminate');
    }
  }

  async function createReport({
    reportID,
    dstEntityName,
    dstProjectName,
  }: CreateReportParams) {
    setCreatingReport(true);
    const crossProjectRunSets = await Report.buildCrossProjectPinsRunSets(
      client,
      runSets,
      {entityName: entityName!, name: projectName!}
    );
    const runSetsWithProject = getRunSetsWithProject();
    const reportSections = getReportSections();
    const reportConfig = getReportConfig();

    try {
      if (reportID === NEW_REPORT_ID) {
        await createNewReport();
        if (!onboardingState.createdFirstReport) {
          setOnboardingState({...onboardingState, createdFirstReport: true});
        }
      } else if (addingToReportSection) {
        await addToReportSection();
      } else {
        await appendSectionsToReport({
          client,
          entityName: dstEntityName,
          projectName: dstProjectName,
          reportID,
          newBlocks: reportConfig.blocks.filter(isPanelGrid).map(b => {
            if (isMetricBasedColors) {
              b.metadata.semanticLegendSettings = {
                ...semanticLegendSettings,
                runColorOptions: RunColorOptions.KeyBasedColors,
              };
            }
            return b;
          }),
          opts: {newTab: true},
          history,
        });
      }
      onCloseModal();
    } catch (err) {
      console.error(`Error creating report`, err);
      setCreatingReport(false);
    }

    function getRunSetsWithProject() {
      if (entityName == null || projectName == null) {
        throw new Error('this should never happen');
      }

      const runSetsWithEmptyDefault =
        runSets.length > 0 ? runSets : [emptyReportRunSetSelectAll()];
      const pinlessRunSets = runSetsWithEmptyDefault.map(rs => ({
        ...rs,
        pinnedRunIds: [],
        baselineRunId: undefined,
        project: rs.project ?? {entityName, name: projectName},
      }));
      return [...pinlessRunSets, ...crossProjectRunSets];
    }

    function getReportSections() {
      if (isSinglePanelExport) {
        for (const s of srcSectionOptions) {
          for (const p of s.panels) {
            if ((p as any).ref?.id === exportingPanelRefID) {
              const exportedPanels = [p];
              const reportSection = Report.createReportSection(
                runSetsWithProject,
                customRunColors,
                customRunNames,
                inheritedSettings?.linePlot,
                s,
                exportedPanels,
                definedGlobs
              );
              return [reportSection];
            }
          }
        }
        return [];
      }

      const selectedSections = srcSectionOptions.filter(o => o.selected);
      const panelsBySection = selectedSections.map(section => {
        let totalPanels = section.panels;
        if (panelIsActive != null) {
          totalPanels = totalPanels.filter(panel =>
            panelIsActive(section, panel)
          );
        }
        totalPanels = filterBySearchRegex(totalPanels);
        return section.visibleOnly
          ? getFirstPagePanels(section.name, section.flowConfig, totalPanels)
          : [...totalPanels];
      });
      const cappedPanelsBySection = takeFirstPanelsBySection(panelsBySection);

      return selectedSections
        .slice(0, cappedPanelsBySection.length)
        .map((section, i) =>
          Report.createReportSection(
            runSetsWithProject,
            customRunColors,
            customRunNames,
            inheritedSettings?.linePlot,
            section,
            cappedPanelsBySection[i],
            definedGlobs
          )
        );
    }

    function getReportConfig() {
      const createReportOpts: CreateReportOpts = {
        mergeFilters,
        runSetName,
      };

      if (freezeRunSet) {
        const runCreatedAtFilter: FilterTypes.Filter<RunTypes.Key> = {
          key: {section: 'run', name: 'createdAt'},
          op: '<=',
          value: currentDate.toISOString(),
          disabled: false,
        };
        if (createReportOpts.mergeFilters != null) {
          createReportOpts.mergeFilters = Filters.And([
            createReportOpts.mergeFilters,
            runCreatedAtFilter,
          ]);
        } else {
          createReportOpts.mergeFilters = runCreatedAtFilter;
        }
      }

      const config =
        reportSections.length > 0
          ? Report.fromSections(
              reportSections,
              createReportOpts,
              undefined,
              definedGlobs
            )
          : Report.getEmptyReportConfig();
      normalizePanelLayouts(config);

      return config;
    }

    async function createNewReport() {
      if (isSinglePanelExport) {
        const panelGrids = reportConfig.blocks.filter(isPanelGrid);
        for (const pg of panelGrids) {
          const config = pg.metadata.panelBankSectionConfig;
          if (isMetricBasedColors) {
            pg.metadata.semanticLegendSettings = {
              ...semanticLegendSettings,
              runColorOptions: RunColorOptions.KeyBasedColors,
            };
          }
          config.panels.forEach(p => (p.layout = getFullWidthPanelLayout()));
        }
      }
      await pushReport({
        client,
        entityName: dstEntityName,
        projectName: dstProjectName,
        config: reportConfig,
        opts: {
          newTab: true,
          redirectQS: {
            runsetFilter: freezeRunSet ? '' : null,
            firstReport: onboardingState.createdFirstReport ? null : '',
          },
        },
        history,
      });
    }

    async function addToReportSection() {
      await editReportPanelGroups({
        client,
        entityName: dstEntityName,
        projectName: dstProjectName,
        reportID,
        opts: {newTab: true},
        editBlocks: blocks => {
          const panelGrids = blocks.filter(isPanelGrid);
          if (selectedDstSectionIdx >= panelGrids.length) {
            blocks.splice(-1, 0, ...reportConfig.blocks.filter(isPanelGrid));
            return;
          }

          if (crossProjectRunSets.length > 0) {
            const destPg = panelGrids[selectedDstSectionIdx];
            const destRunSets = destPg.metadata.runSets ?? [];
            destPg.metadata.runSets = [...destRunSets, ...crossProjectRunSets];
          }

          const panelBankSectionConfig =
            panelGrids[selectedDstSectionIdx].metadata.panelBankSectionConfig;
          // ensure panel layouts
          const panelLayouts: LayoutParameters[] = [];
          for (const p of panelBankSectionConfig.panels) {
            p.layout = p.layout ?? getNewGridItemLayout(panelLayouts);
            panelLayouts.push(p.layout);
          }
          panelBankSectionConfig.panels = [
            ...panelBankSectionConfig.panels,
            {
              ...reportSections[0].panelBankSectionConfig.panels[0],
              layout: getNewGridItemLayout(
                panelBankSectionConfig.panels.map(p => p.layout)
              ),
            },
          ];
        },
        history,
      });
    }
  }

  function createNewReportInCurrentProject() {
    if (entityName == null || projectName == null) {
      throw new Error('this should never happen');
    }
    createReport({
      reportID: NEW_REPORT_ID,
      dstEntityName: entityName,
      dstProjectName: projectName,
    });
  }

  function createReportInSelectedProject(reportID: string) {
    const projectInfo = projectInfoByID[selectedProjectID];
    if (projectInfo == null) {
      return;
    }
    const {entityName: dstEntityName, name: dstProjectName} = projectInfo;
    createReport({
      reportID,
      dstEntityName,
      dstProjectName,
    });
  }

  function createReportOrCheckDraft() {
    if (selectedReportID === NEW_REPORT_ID) {
      createReportInSelectedProject(selectedReportID);
    } else {
      setDraftWarningModalOpen(true);
    }
  }

  function renderIntroModal() {
    const introExamples = [
      {
        text: 'Add quick notes to a chart.',
        src: introGraphicNoteToSelf,
      },
      {
        text: "Track what you've tried.",
        src: introGraphicWorkLog,
      },
      {
        text: 'Collaborate with your team.',
        src: introGraphicNewFindings,
      },
    ];

    return (
      <>
        <S.WBModalHeader>
          Reports = Google Docs + Live Data
          <div>Here are a few examples of how you can use Reports:</div>
        </S.WBModalHeader>
        <S.WBModalContent>
          <S.WBModalImageGallery>
            {introExamples.map(e => (
              <div key={e.src}>
                <img src={e.src} alt="intro-report-graphic" />
                <div>{e.text}</div>
              </div>
            ))}
          </S.WBModalImageGallery>
        </S.WBModalContent>
        <S.WBModalActions>
          <S.WBModalButton onClick={onCloseModal}>Cancel</S.WBModalButton>
          <S.WBModalButton primary onClick={() => setModalPage('import')}>
            Next: Import Charts
          </S.WBModalButton>
        </S.WBModalActions>
      </>
    );
  }

  // TODO - remove this when refactoring and triple check that it isn't being used anywhere else
  function renderImportModal() {
    return (
      <>
        <S.WBModalHeader>Create Report</S.WBModalHeader>
        <S.WBModalContent>
          {showPanelLimitWarning && <ReportImportPanelLimitBanner />}
          {sectionsWithVisiblePanels.length === 0 ? (
            <p>
              You have no panels on this page, so this will create an empty
              report.
            </p>
          ) : (
            <>
              <S.WBModalContentSection>
                <S.CheckboxModalContentHeader>
                  <Checkbox
                    checked={selectAllSectionOptions}
                    onCheckedChange={() => toggleSelectAllSectionOptions()}
                  />
                  Import all charts
                </S.CheckboxModalContentHeader>
                {srcSectionOptions.map(opts => (
                  <div key={opts.name}>{renderSectionOption(opts)}</div>
                ))}
              </S.WBModalContentSection>
              <S.WBModalContentSection>
                <S.WBModalContentHeader>Filter run sets</S.WBModalContentHeader>
                <S.ImportModalSection>
                  <Checkbox
                    checked={freezeRunSet}
                    onCheckedChange={() => setFreezeRunSet(prev => !prev)}
                  />
                  {`Only include runs created before ${currentDate.toLocaleTimeString()}`}
                  <Popup
                    inverted
                    size="mini"
                    position="top center"
                    trigger={<Icon name="info" />}>
                    You can change this anytime for individual run sets with the
                    "freeze run set" button
                  </Popup>
                </S.ImportModalSection>
              </S.WBModalContentSection>
            </>
          )}
        </S.WBModalContent>
        <S.ImportModalActions>
          <S.ModalButton
            disabled={creatingReport}
            onClick={() => {
              Analytics.track('Create Report Clicked', {
                entityName,
                projectName,
                location: 'create report modal',
                isSelectAll: selectAllSectionOptions === true,
              });
              createNewReportInCurrentProject();
            }}>
            Create report
          </S.ModalButton>
          <S.ModalButton
            variant="ghost"
            onClick={
              !onboardingState.createdFirstReport
                ? () => setModalPage('intro')
                : onCloseModal
            }>
            {!onboardingState.createdFirstReport ? 'Back' : 'Cancel'}
          </S.ModalButton>
        </S.ImportModalActions>
      </>
    );
  }

  function renderSectionOption(sectionOpt: SrcSectionOption) {
    const panelCount =
      sectionOpt.selected && sectionOpt.visibleOnly
        ? sectionOpt.visiblePanelCount
        : sectionOpt.totalPanelCount;
    const panelNoun = panelCount === 1 ? 'panel' : 'panels';

    return (
      <>
        <S.ImportModalSection>
          <Checkbox
            checked={sectionOpt.selected}
            onCheckedChange={() => toggleSrcSectionOption(sectionOpt)}
          />
          <div>
            <span>{sectionOpt.name}</span>
            <span className="section-panel-count">
              {` (${panelCount} ${panelNoun})`}
            </span>
          </div>
        </S.ImportModalSection>
        {sectionOpt.selected && (
          <S.ImportModalSectionOption>
            <Checkbox
              checked={sectionOpt.visibleOnly}
              onCheckedChange={() => toggleSrcSectionOption(sectionOpt, true)}
            />
            <span>Only import the first page of the section</span>
          </S.ImportModalSectionOption>
        )}
      </>
    );
  }

  function renderExportModal() {
    return (
      <TailwindContents>
        <div className="flex h-full w-[100vw] flex-col pb-20 pl-20 pr-20 pt-drawer-top md:w-[600px]">
          <div className="flex items-center justify-between pb-18 pt-18">
            <h2 className="text-2xl font-semibold">Add to report</h2>
            <Button
              onClick={onCloseModal}
              icon="close"
              size="large"
              variant="ghost"
            />
          </div>
          <div className="-ml-20 -mr-20 mb-4 border-b border-wb-border"></div>
          {showPanelLimitWarning && <ReportImportPanelLimitBanner />}
          <div className="flex flex-col">
            <div className="mb-4 mt-16 text-base font-semibold">Entity</div>
            <ModifiedDropdown
              selection
              search
              options={entityOptions}
              value={selectedEntity}
              onChange={onEntityChange}
              loading={loading}
            />
            <div className="mb-4 mt-16 text-base font-semibold">Project</div>
            <ModifiedDropdown
              selection
              search
              fluid
              searchQuery={destinationProjectSearchStr}
              onSearchChange={(e, data) =>
                setDestinationProjectSearchStr(data.searchQuery)
              }
              options={projectOptions}
              value={selectedProjectID}
              onChange={onProjectChange}
              loading={loading}
            />
            <div className="mb-4 mt-16 text-base font-semibold">Report</div>
            <ModifiedDropdown
              selection
              fluid
              options={reportOptionsByProjectID[selectedProjectID] ?? []}
              value={selectedReportID}
              onChange={onReportChange}
              search
              searchQuery={reportSearchQuery}
              onSearchChange={(e, data) =>
                setReportSearchQuery(data.searchQuery)
              }
            />
            {selectedReportID !== NEW_REPORT_ID && (
              <>
                <div className="mb-4 mt-16 text-base font-semibold">
                  Panel Grid
                </div>
                <ReportSectionPicker
                  reportID={selectedReportID}
                  selectedSectionIdx={selectedDstSectionIdx}
                  className="save-snapshot-modal__select"
                  onSelectSection={onDstSectionChange}
                />
              </>
            )}
          </div>
          <div className="flex-grow-1 mt-24 flex h-full w-full items-end gap-8">
            <div className="w-full">
              <div className="-ml-20 -mr-20 mb-4 border-b border-wb-border"></div>
              <Button
                size="large"
                className="mt-20 w-full"
                disabled={creatingReport}
                onClick={createReportOrCheckDraft}>
                Add to report
              </Button>
            </div>
          </div>
        </div>
      </TailwindContents>
    );
  }

  function getDraftWarningModalProps(): Pick<
    DraftWarningModalProps,
    'mainText' | 'discardText' | 'renderResumeButton'
  > {
    if (addingToReportSection) {
      return {
        mainText:
          'You have unsaved edits to this report. Do you want to discard unsaved edits and import new panels?',
        discardText: 'Import panels',
        renderResumeButton: () => (
          <Button
            size="large"
            variant="ghost"
            onClick={() => setDraftWarningModalOpen(false)}>
            Go back
          </Button>
        ),
      };
    }
    return {
      mainText:
        'You have unsaved edits to this report. Do you want to resume or discard these unsaved edits?',
      discardText: 'Discard',
      renderResumeButton: (
        draftID: string,
        resuming: boolean,
        discarding: boolean,
        setResuming: (r: boolean) => void
      ) => (
        <Button
          size="large"
          variant="ghost"
          disabled={resuming || discarding}
          onClick={() => {
            setResuming(true);
            createReportInSelectedProject(draftID);
          }}>
          Resume
        </Button>
      ),
    };
  }
};

export const CreateReportModalContent = memo(CreateReportModalContentComp);
